import React, { useState, useMemo } from 'react';
import { getCategoriesForSelect, searchCategories, getCategoryName } from '../../../utils/categories';
import './CategorySelector.scss';

export interface CategorySelectorProps {
  /** Selected category IDs */
  value: string[];
  /** Callback when selection changes */
  onChange: (categoryIds: string[]) => void;
  /** Language for display */
  language?: 'tr' | 'en' | 'de';
  /** Placeholder text */
  placeholder?: string;
  /** Whether to allow multiple selection */
  multiple?: boolean;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** CSS class name */
  className?: string;
  /** Whether to show search functionality */
  searchable?: boolean;
  /** Error message */
  error?: string;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  value = [],
  onChange,
  language = 'tr',
  placeholder = 'Select categories...',
  multiple = true,
  disabled = false,
  className = '',
  searchable = true,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get categories based on search query
  const filteredCategories = useMemo(() => {
    const allCategories = getCategoriesForSelect(language);
    
    if (!searchQuery.trim()) {
      return allCategories;
    }
    
    return searchCategories(searchQuery, language);
  }, [searchQuery, language]);

  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    if (disabled) return;

    if (multiple) {
      const newValue = value.includes(categoryId)
        ? value.filter(id => id !== categoryId)
        : [...value, categoryId];
      onChange(newValue);
    } else {
      onChange([categoryId]);
      setIsOpen(false);
    }
  };

  // Handle remove category
  const handleRemoveCategory = (categoryId: string) => {
    if (disabled) return;
    onChange(value.filter(id => id !== categoryId));
  };

  // Handle clear all
  const handleClearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  // Get display text for selected categories
  const getDisplayText = () => {
    if (value.length === 0) {
      return placeholder;
    }
    
    if (multiple) {
      if (value.length === 1) {
        return getCategoryName(value[0], language);
      }
      return `${value.length} categories selected`;
    }
    
    return getCategoryName(value[0], language);
  };

  return (
    <div className={`category-selector ${className} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}>
      {/* Selected Categories Display */}
      <div 
        className="category-selector__trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="category-selector__value">
          {value.length > 0 && multiple ? (
            <div className="category-selector__selected">
              {value.slice(0, 2).map(categoryId => (
                <span key={categoryId} className="category-selector__tag">
                  {getCategoryName(categoryId, language)}
                  <button
                    type="button"
                    className="category-selector__remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCategory(categoryId);
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
              {value.length > 2 && (
                <span className="category-selector__more">
                  +{value.length - 2} more
                </span>
              )}
            </div>
          ) : (
            <span className="category-selector__placeholder">
              {getDisplayText()}
            </span>
          )}
        </div>
        
        <div className="category-selector__actions">
          {value.length > 0 && (
            <button
              type="button"
              className="category-selector__clear"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              title="Clear all"
            >
              ×
            </button>
          )}
          <span className={`category-selector__arrow ${isOpen ? 'open' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="category-selector__dropdown">
          {searchable && (
            <div className="category-selector__search">
              <input
                type="text"
                placeholder="Search categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="category-selector__search-input"
                autoFocus
              />
            </div>
          )}
          
          <div className="category-selector__options">
            {filteredCategories.length === 0 ? (
              <div className="category-selector__no-results">
                No categories found
              </div>
            ) : (
              filteredCategories.map(category => (
                <div
                  key={category.value}
                  className={`category-selector__option ${
                    value.includes(category.value) ? 'selected' : ''
                  }`}
                  onClick={() => handleCategorySelect(category.value)}
                >
                  <div className="category-selector__option-content">
                    <div className="category-selector__option-name">
                      {category.label}
                    </div>
                    {category.description && (
                      <div className="category-selector__option-description">
                        {category.description}
                      </div>
                    )}
                  </div>
                  {multiple && value.includes(category.value) && (
                    <div className="category-selector__check">
                      ✓
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="category-selector__error">
          {error}
        </div>
      )}
    </div>
  );
};

export default CategorySelector;



